import { useMemo, useState } from "react";
import {
  Typography,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Box,
  Stack,
  Paper,
  Chip,
} from "@mui/material";
import { ExitToApp, Refresh, TableChart, Close } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { getHoldColumns } from "../holdGridConfig";
import { TradeTypes } from "../../../config/constants";
import { useDataFetch } from "../../../core/hooks/useDataFetch";
import { Config } from "../../../config/config";
import { useResponsive } from "../../../core/hooks/useResponsive";
import BuyPerformanceDialog from "./BuyPerformanceDialog";
import HoldingCard from "./HoldingCard";

const CARD_PAGE_SIZE = 8;

const SORT_OPTIONS = [
  { key: "plDesc", label: "P/L high→low" },
  { key: "plAsc", label: "P/L low→high" },
  { key: "symbol", label: "A→Z" },
];

export const Holdings = ({ data, onRefresh }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [buyPerformanceDialogOpen, setBuyPerformanceDialogOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [animatingRowId, setAnimatingRowId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(CARD_PAGE_SIZE);
  const [sortKey, setSortKey] = useState("plDesc");
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { callAPI } = useDataFetch();
  const { isPhone, isCompact, isTablet } = useResponsive();

  const rows = useMemo(() => data || [], [data]);

  // Selection lives as ids so it survives a rotate between grid and card rendering.
  const selectedKeys = useMemo(() => new Set(selectedIds.map(String)), [selectedIds]);
  const selectedRows = useMemo(
    () => rows.filter((r) => selectedKeys.has(String(r.tradeId))),
    [rows, selectedKeys]
  );
  // Feed the grid only ids that still exist in `rows` — after a refresh drops a sold
  // position, a stale id in a controlled selection model makes the grid complain.
  const gridSelectionModel = useMemo(() => selectedRows.map((r) => r.tradeId), [selectedRows]);

  // Card mode has no column headers to sort by, so the grid's Profit/Loss sort is
  // replaced with explicit chips rather than quietly lost.
  const sortedRows = useMemo(() => {
    const out = [...rows];
    if (sortKey === "plDesc") out.sort((a, b) => (Number(b.profitLoss) || 0) - (Number(a.profitLoss) || 0));
    else if (sortKey === "plAsc") out.sort((a, b) => (Number(a.profitLoss) || 0) - (Number(b.profitLoss) || 0));
    else if (sortKey === "symbol") out.sort((a, b) => String(a.symbol || "").localeCompare(String(b.symbol || "")));
    return out;
  }, [rows, sortKey]);

  const openPerformance = (row) => {
    setSelectedRowData(row);
    setBuyPerformanceDialogOpen(true);
  };

  const handleRowDoubleClick = (params) => {
    setAnimatingRowId(params.id);
    setTimeout(() => {
      openPerformance(params.row);
      setAnimatingRowId(null);
    }, 220);
  };

  const handleCloseBuyPerformanceDialog = () => {
    setBuyPerformanceDialogOpen(false);
    setSelectedRowData(null);
  };

  const toggleCardSelection = (tradeId) => {
    setSelectedIds((prev) => {
      const key = String(tradeId);
      return prev.some((p) => String(p) === key)
        ? prev.filter((p) => String(p) !== key)
        : [...prev, tradeId];
    });
  };

  const handleExitTrade = async () => {
    const payload = {
      tradeData: selectedRows.map((r) => ({
        tradeId: r.tradeId,
        token: r.token,
        symbol: r.symbol,
        qty: parseInt(r.qty, 10),
        price: r.marketPrice,
      })),
      tradeType: TradeTypes.SALE,
    };
    await callAPI(Config.ITradeAPI.tradeOrders.url, payload);
    // Those rows are gone after the refresh; keeping their ids selected would leave
    // the Exit button armed against nothing.
    setSelectedIds([]);
    onRefresh();
  };

  const selectedValue = selectedRows.reduce(
    (sum, r) => sum + (Number(r.marketValue) || Number(r.qty) * Number(r.marketPrice) || 0),
    0
  );

  const exitButton = (
    <Button
      variant="contained"
      color="error"
      size={isPhone ? "small" : "medium"}
      startIcon={<ExitToApp />}
      onClick={handleExitTrade}
      disabled={selectedRows.length === 0}
      sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
    >
      Exit trade
    </Button>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: `1px solid ${alpha(primary, 0.14)}`,
        boxShadow: `0 8px 28px ${alpha("#000", 0.06)}`,
      }}
    >
      {/* Header. On phones the title row keeps only an icon Refresh, and Exit trade moves
          into a selection bar that appears right above the cards it acts on. */}
      <Box
        sx={{
          px: { xs: 1.25, md: 1.5 },
          py: { xs: 1, md: 1.25 },
          bgcolor: alpha(primary, 0.06),
          borderBottom: `1px solid ${alpha(primary, 0.1)}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: isPhone ? 32 : 40,
                height: isPhone ? 32 : 40,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(primary, 0.14),
                color: primary,
                flexShrink: 0,
              }}
            >
              <TableChart fontSize={isPhone ? "small" : "medium"} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={800} letterSpacing="-0.02em" noWrap>
                Live holdings
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                {isCompact
                  ? `${rows.length} position${rows.length === 1 ? "" : "s"} · tap a card for detail`
                  : "Double-click row for buy performance"}
              </Typography>
            </Box>
          </Stack>

          {isPhone ? (
            <Tooltip title="Refresh holdings">
              <IconButton
                onClick={onRefresh}
                size="small"
                sx={{ bgcolor: alpha(primary, 0.12), color: primary, "&:hover": { bgcolor: alpha(primary, 0.2) } }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {exitButton}
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={onRefresh}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  bgcolor: alpha(primary, 0.12),
                  color: primary,
                  boxShadow: "none",
                  "&:hover": { bgcolor: alpha(primary, 0.2), boxShadow: "none" },
                }}
              >
                Refresh
              </Button>
            </Stack>
          )}
        </Stack>

        {/* Sort chips — card mode only; restores the sort that column headers provide. */}
        {isCompact && rows.length > 1 && (
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, overflowX: "auto", pb: 0.25 }}>
            {SORT_OPTIONS.map((opt) => (
              <Chip
                key={opt.key}
                label={opt.label}
                size="small"
                onClick={() => setSortKey(opt.key)}
                variant={sortKey === opt.key ? "filled" : "outlined"}
                color={sortKey === opt.key ? "primary" : "default"}
                sx={{ fontWeight: 700, fontSize: "0.7rem", flexShrink: 0 }}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* Selection action bar — phone only, sits directly above the selected cards. */}
      {isPhone && selectedRows.length > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{
            px: 1.25,
            py: 1,
            bgcolor: alpha(primary, 0.1),
            borderBottom: `1px solid ${alpha(primary, 0.16)}`,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" fontWeight={800} sx={{ display: "block", lineHeight: 1.2 }}>
              {selectedRows.length} selected
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", fontSize: "0.68rem" }}>
              ≈ {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(selectedValue)}
            </Typography>
          </Box>
          {exitButton}
          <Tooltip title="Clear selection">
            <IconButton size="small" onClick={() => setSelectedIds([])}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )}

      {isCompact ? (
        /* --- Card list (<768px) --- */
        <Box sx={{ p: 1, bgcolor: alpha(theme.palette.background.default, 0.35) }}>
          {sortedRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
              No open holdings.
            </Typography>
          ) : (
            <>
              <Stack spacing={1}>
                {sortedRows.slice(0, visibleCount).map((row) => (
                  <HoldingCard
                    key={row.tradeId}
                    row={row}
                    selected={selectedKeys.has(String(row.tradeId))}
                    onToggle={toggleCardSelection}
                    onOpen={openPerformance}
                  />
                ))}
              </Stack>
              {visibleCount < sortedRows.length && (
                <Button
                  fullWidth
                  size="small"
                  onClick={() => setVisibleCount((c) => c + CARD_PAGE_SIZE)}
                  sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}
                >
                  Show {Math.min(CARD_PAGE_SIZE, sortedRows.length - visibleCount)} more
                  {` (${visibleCount} of ${sortedRows.length})`}
                </Button>
              )}
            </>
          )}
        </Box>
      ) : (
        /* --- DataGrid (>=768px); folded column set on tablet --- */
        <Box sx={{ p: { xs: 1, md: 1.25 }, bgcolor: alpha(theme.palette.background.default, 0.35) }}>
          <DataGrid
            rows={rows}
            columns={getHoldColumns({ folded: isTablet })}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            pageSizeOptions={[10, 15, 20]}
            checkboxSelection
            getRowId={(row) => row.tradeId}
            rowHeight={isTablet ? 62 : 42}
            columnHeaderHeight={44}
            rowSelectionModel={gridSelectionModel}
            onRowSelectionModelChange={(model) => setSelectedIds(model)}
            onRowDoubleClick={handleRowDoubleClick}
            sx={{
              border: "none",
              borderRadius: 1.5,
              bgcolor: theme.palette.background.paper,
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: alpha(primary, 0.08),
                color: primary,
                fontWeight: 800,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              },
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 800, color: primary },
              "& .MuiDataGrid-columnHeader .MuiSvgIcon-root": { color: primary, opacity: 1 },
              "& .MuiDataGrid-iconButtonContainer .MuiSvgIcon-root": { color: primary },
              "& .MuiDataGrid-menuIcon .MuiSvgIcon-root": { color: primary },
              "& .MuiDataGrid-sortIcon": { color: primary },
              "& .MuiDataGrid-filterIcon": { color: primary },
              // The grid hides these by default (visibility/opacity 0) until hover or
              // an active sort/filter. Sort button only exists on Profit/Loss (the
              // only sortable column) so forcing it visible is already scoped; the
              // menu icon exists on every column, so scope that one specifically to
              // Profit/Loss via data-field to avoid changing other columns' look.
              "& .MuiDataGrid-sortButton": { opacity: "1 !important" },
              '& .MuiDataGrid-columnHeader[data-field="profitLossCombined"] .MuiDataGrid-iconButtonContainer': {
                visibility: "visible !important",
                width: "auto !important",
              },
              '& .MuiDataGrid-columnHeader[data-field="profitLossCombined"] .MuiDataGrid-menuIcon': {
                visibility: "visible !important",
                width: "auto !important",
              },
              "& .MuiDataGrid-row:hover": {
                bgcolor: alpha(primary, 0.12),
                transition: "background-color 0.18s ease",
              },
              "& .row-open-animate": {
                animation: "rowDoubleClickPulse 0.22s ease",
              },
              "@keyframes rowDoubleClickPulse": {
                "0%": { transform: "scale(1)", backgroundColor: alpha(primary, 0.05) },
                "50%": { transform: "scale(0.995)", backgroundColor: alpha(primary, 0.18) },
                "100%": { transform: "scale(1)", backgroundColor: alpha(primary, 0.08) },
              },
              "& .MuiDataGrid-cell": { borderColor: alpha(theme.palette.divider, 0.6) },
              "& .profit-cell": { color: theme.palette.success?.main || "#16a34a", fontWeight: 700 },
              "& .loss-cell": { color: theme.palette.error?.main || "#dc2626", fontWeight: 700 },
              "& .neutral-cell": { color: theme.palette.text.secondary },
            }}
            getRowClassName={(params) => (params.id === animatingRowId ? "row-open-animate" : "")}
          />
        </Box>
      )}

      <BuyPerformanceDialog open={buyPerformanceDialogOpen} onClose={handleCloseBuyPerformanceDialog} rowData={selectedRowData} />
    </Paper>
  );
};
